import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Table from '../common/Table';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  
  const [formData, setFormData] = useState({
    branch_name: '',
    location: '',
    phone: '',
    email: '',
    is_main: false,
    is_active: true
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/branches.php');
      setBranches(response.data.branches || []);
    } catch (error) {
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      branch_name: branch.branch_name,
      location: branch.location || '',
      phone: branch.phone || '',
      email: branch.email || '',
      is_main: !!branch.is_main,
      is_active: !!branch.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this branch?')) {
      try {
        await api.delete(`/branches.php/${id}`);
        toast.success('Branch deactivated successfully');
        fetchBranches();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to deactivate branch');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await api.put(`/branches.php/${editingBranch.id}`, formData);
        toast.success('Branch updated successfully');
      } else {
        await api.post('/branches.php', formData);
        toast.success('Branch created successfully');
      }
      setShowModal(false);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save branch');
    }
  };

  const columns = [
    { key: 'branch_name', label: 'Branch Name' },
    { key: 'location', label: 'Location' },
    { key: 'phone', label: 'Phone' },
    { 
      key: 'is_main', 
      label: 'Main Branch', 
      render: (val) => val ? <span className="badge success">Yes</span> : <span className="badge secondary">No</span>
    },
    { 
      key: 'is_active', 
      label: 'Status', 
      render: (val) => val ? <span className="badge success">Active</span> : <span className="badge danger">Inactive</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button className="icon-btn edit" onClick={() => handleEdit(row)}>✏️</button>
          {!row.is_main && (
            <button className="icon-btn delete" onClick={() => handleDelete(row.id)}>🗑️</button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="branches-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3>Business Branches</h3>
        <Button onClick={() => {
          setEditingBranch(null);
          setFormData({ branch_name: '', location: '', phone: '', email: '', is_main: false, is_active: true });
          setShowModal(true);
        }} icon="➕">
          Add Branch
        </Button>
      </div>

      <Table 
        columns={columns} 
        data={branches} 
        loading={loading} 
        emptyMessage="No branches found."
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingBranch ? 'Edit Branch' : 'Add Branch'}
      >
        <form onSubmit={handleSubmit} className="form-grid">
          <Input
            label="Branch Name"
            value={formData.branch_name}
            onChange={e => setFormData({...formData, branch_name: e.target.value})}
            required
          />
          <Input
            label="Location / Address"
            value={formData.location}
            onChange={e => setFormData({...formData, location: e.target.value})}
          />
          <Input
            label="Phone"
            value={formData.phone}
            onChange={e => setFormData({...formData, phone: e.target.value})}
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={e => setFormData({...formData, email: e.target.value})}
          />
          
          <div className="checkbox-group" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
            <label>
              <input 
                type="checkbox" 
                checked={formData.is_main} 
                onChange={e => setFormData({...formData, is_main: e.target.checked})} 
              />
              Main Branch (Headquarters)
            </label>
            {editingBranch && (
              <label>
                <input 
                  type="checkbox" 
                  checked={formData.is_active} 
                  onChange={e => setFormData({...formData, is_active: e.target.checked})} 
                />
                Active Status
              </label>
            )}
          </div>

          <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary">{editingBranch ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Branches;
